import { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import style from './style.m.less'
import useRequest, { RequestTypes } from '../hooks/useRequest'
import {
    CalculatorAuditValues,
    CalculatorDropdownData,
    FormHandle,
    PollutantsResultResponse,
} from './types'
import { EnergyForm, PollutantsForm, ThermalForm } from './components'
import { Tabs } from 'antd'
import useWindowWidth from '../hooks/useWindowWidth'

const { TabPane } = Tabs

const Calculator: FC = () => {
    const windowWidth = useWindowWidth()
    const isMobile = windowWidth <= 700

    const { t, i18n } = useTranslation()
    const locale = i18n.language.includes('ru') ? 'ru' : 'en'

    const energyFormRef = useRef<FormHandle>(null)
    const thermalFormRef = useRef<FormHandle>(null)
    const pollutantsFormRef = useRef<FormHandle>(null)

    const [data, setData] = useState<CalculatorDropdownData | null>(null)
    const [error, setError] = useState('')
    const [auditId, setAuditId] = useState(-1)

    const [currentOption, setCurrentOption] = useState('0')
    const tabsList = [
        { label: t('calculator.energyTitle'), key: 0, content: <></> /*<EnergyForm />*/ },
        { label: t('calculator.thermalTitle'), key: 1, content: <></> /*<ThermalForm />*/ },
        { label: t('calculator.pollutantsTitle'), key: 2, content: <></> /*<ThermalForm />*/ },
    ]

    const { fetch: fetchDropdownData, result: resDropdownData } = useRequest({
        link: '/api/extension/v1/extension/calculator/dropdown_data/',
        method: RequestTypes.get,
    })
    const { fetch: fetchCalculate, result: resCalculate } = useRequest({
        link: '/api/extension/v1/extension/calculator/calculate',
        method: RequestTypes.post,
    })
    const { fetch: fetchCalculatorAudit, result: resCalculatorAudit } = useRequest({
        link: '/api/extension/v1/extension/calculator/audit',
        method: RequestTypes.get,
    })
    const { fetch: fetchCalculatorAuditUpdate, isLoading: isLoadingAuditUpdate } = useRequest({
        link: '/api/extension/v1/extension/calculator/audit/update',
        method: RequestTypes.post,
    })
    const { fetch: fetchThermalCalculate, result: resThermalCalculate } = useRequest({
        link: '/api/extension/v1/extension/calculator/thermal/calculate',
        method: RequestTypes.post,
    })
    const { fetch: fetchPollutantsCalculate, result: resPollutantsCalculate } =
        useRequest<PollutantsResultResponse>({
            link: '/api/extension/v1/extension/calculator/pollutants',
            method: RequestTypes.post,
        })

    useEffect(() => {
        fetchDropdownData({ addToLink: `?locale=${locale}` })
        fetchCalculatorAudit({
            onSuccess: (data) => {
                setAuditId(data?.detail?.id ? data.detail.id : -1)
            },
        })
    }, [])

    // Загрузка начальных данных
    useEffect(() => {
        try {
            const response = resDropdownData
            if (response?.status === 'OK') {
                setData(response.detail)
            }
        } catch (err) {
            setError(t('calculator.errors.dropdownData'))
        }
    }, [resDropdownData])

    const scrollToMain = () => {
        document
            .getElementById('calculator_main')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const auditUpdate = (auditData: CalculatorAuditValues) => {
        fetchCalculatorAuditUpdate({
            body: auditData,
        })
    }

    const updateAuditId = (values: any, onSuccess: (values: any, data: any) => void): void => {
        fetchCalculatorAudit({
            onSuccess: (data) => {
                setAuditId(data?.detail?.id)
                onSuccess(values, data)
            },
        })
    }

    const callFetchEnergyCalculate = (body: any, onSuccess: () => void) => {
        fetchCalculate({
            body: body,
            onSuccess: onSuccess,
        })
    }
    const callFetchThermalCalculate = (body: any) => {
        fetchThermalCalculate({
            body: body,
        })
    }
    const callFetchPollutantsCalculate = (body: any) => {
        fetchPollutantsCalculate({
            body: body,
        })
    }

    const resetAudit = () => {
        setAuditId(-1)
    }

    const handleFormsReset = () => {
        energyFormRef.current?.resetForm()
        thermalFormRef.current?.resetForm()
        pollutantsFormRef.current?.resetForm()
    }
    return (
        <>
            <div className={style.mainScreen}>
                <div className={style.container}>
                    <h1 className={style.title}>{t('calculator.title')}</h1>
                </div>
            </div>
            <div className={style.calculatorMain}>
                <div className={style.formDescription}>{t('calculator.description')}</div>
                <div className={style.methodBlock}>
                    <a href="/documents/calcMethod.pdf" download target="_blank">
                        {t('calculator.methodFile')}
                    </a>
                </div>
                <Tabs
                    id="calculator_main"
                    defaultActiveKey="0"
                    onChange={(activeKey) => {
                        setCurrentOption(activeKey)
                        handleFormsReset()
                    }}
                    activeKey={currentOption}
                    className={style.tabs}
                    tabPosition={isMobile ? 'left' : 'top'}
                >
                    {tabsList.map((item) => {
                        const { label, key, content } = item

                        return (
                            <TabPane tab={label} key={key}>
                                {content}
                            </TabPane>
                        )
                    })}
                </Tabs>
                {currentOption === '0' && (
                    <EnergyForm
                        ref={energyFormRef}
                        data={data}
                        auditId={auditId}
                        error={error}
                        resCalculate={resCalculate}
                        isLoadingAuditUpdate={isLoadingAuditUpdate}
                        resCalculatorAudit={resCalculatorAudit}
                        setError={setError}
                        scrollToMain={scrollToMain}
                        auditUpdate={auditUpdate}
                        updateAuditId={updateAuditId}
                        fetchCalculate={callFetchEnergyCalculate}
                        resetAudit={resetAudit}
                    />
                )}
                {currentOption === '1' && (
                    <ThermalForm
                        ref={thermalFormRef}
                        data={data}
                        error={error}
                        resCalculate={resThermalCalculate}
                        setError={setError}
                        scrollToMain={scrollToMain}
                        fetchCalculate={callFetchThermalCalculate}
                    />
                )}
                {currentOption === '2' && (
                    <PollutantsForm
                        ref={pollutantsFormRef}
                        error={error}
                        setError={setError}
                        scrollToMain={scrollToMain}
                        resCalculate={resPollutantsCalculate}
                        fetchCalculate={callFetchPollutantsCalculate}
                    />
                )}

                <div className={style.formDescription}>
                    {t('calculator.subtitleConclusion')}
                    <a
                        href="https://www.np-sr.ru/ru/market/scope/index.htm"
                        target="_blank"
                        rel="noreferrer"
                    >
                        {t('calculator.linkConclusion')}
                    </a>
                </div>
            </div>
        </>
    )
}

export default Calculator
